import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Plus, Dumbbell, Clock, Target, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkoutPlanner = ({ token }) => {
  const { t } = useTranslation();
  const [workouts, setWorkouts] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAddWorkout, setShowAddWorkout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchWorkoutPlan();
  }, []);

  const fetchWorkoutPlan = async () => {
    try {
      const response = await axios.get(`${API}/exercise/workout-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkouts(response.data.plan || {});
    } catch (error) {
      console.error('Error fetching workout plan:', error);
    }
  };

  const generateAIPlan = async () => {
    if (!window.confirm('Generate a new AI workout plan? This will replace your current plan.')) return;

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/exercise/generate-plan`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkouts(response.data.plan || {});
      alert('✅ AI Workout Plan generated!');
    } catch (error) {
      console.error('Error generating plan:', error);
      alert('❌ Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const addWorkout = async (day, workout) => {
    try {
      await axios.post(
        `${API}/exercise/workout-plan`,
        { day, workout },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkoutPlan();
      setShowAddWorkout(false);
      setSelectedDay(null);
    } catch (error) {
      console.error('Error adding workout:', error);
      alert('Failed to add workout');
    }
  };

  const deleteWorkout = async (day, workoutIndex) => {
    try {
      await axios.delete(`${API}/exercise/workout-plan`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { day, workout_index: workoutIndex }
      });
      fetchWorkoutPlan();
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-8" data-testid="workout-planner">
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Calendar size={20} className="text-[#FF6600]" />
            Weekly Workout Planner
          </h2>
          <button
            onClick={generateAIPlan}
            disabled={generating}
            className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] font-semibold text-sm disabled:opacity-50"
            data-testid="generate-ai-plan-btn"
          >
            {generating ? 'Generating...' : '🤖 Generate AI Plan'}
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Plan your weekly workouts or let AI create a personalized plan for you!
        </p>
      </div>

      {/* Weekly Calendar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {days.map((day) => (
          <div key={day} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-[#FF6600] text-white p-3">
              <h3 className="font-bold">{day}</h3>
              <p className="text-xs opacity-90">
                {workouts[day]?.length || 0} workout{workouts[day]?.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="p-3">
              {/* Workouts for this day */}
              {workouts[day] && workouts[day].length > 0 ? (
                <div className="space-y-2 mb-3">
                  {workouts[day].map((workout, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 p-3 rounded-lg relative group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-sm text-gray-900 dark:text-white mb-1">{workout.name}</h4>
                          <div className="flex gap-3 text-xs text-gray-600 dark:text-gray-300">
                            <span className="flex items-center gap-1">
                              <Target size={12} />
                              {workout.sets} sets
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {workout.duration} min
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteWorkout(day, idx)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-6 text-xs">Rest day or no workouts planned</p>
              )}

              {/* Add Workout Button */}
              <button
                onClick={() => {
                  setSelectedDay(day);
                  setShowAddWorkout(true);
                }}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 dark:text-gray-300 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors text-sm font-semibold"
              >
                <Plus size={14} className="inline mr-1" />
                Add Workout
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Workout Modal */}
      {showAddWorkout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full">
            <h3 className="font-bold mb-4">Add Workout to {selectedDay}</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const workout = {
                  name: formData.get('name'),
                  sets: parseInt(formData.get('sets')),
                  reps: formData.get('reps'),
                  duration: parseInt(formData.get('duration'))
                };
                addWorkout(selectedDay, workout);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2 text-sm">Exercise Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                    placeholder="e.g. Bench Press"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold mb-2 text-sm">Sets</label>
                    <input
                      name="sets"
                      type="number"
                      required
                      min="1"
                      defaultValue="3"
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2 text-sm">Reps</label>
                    <input
                      name="reps"
                      type="text"
                      required
                      defaultValue="10-12"
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-semibold mb-2 text-sm">Duration (minutes)</label>
                  <input
                    name="duration"
                    type="number"
                    required
                    min="1"
                    defaultValue="30"
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddWorkout(false);
                    setSelectedDay(null);
                  }}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] font-semibold"
                >
                  Add Workout
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutPlanner;
